import { lru } from 'tiny-lru';

const defaultLRUOptions = {
    max: 1000,
};
/**
 * Returns a memorised version of the target function.
 * The cache key depends on the arguments passed to the function.
 *
 * Pass in options to customise the caching behaviour
 * @param cachedFn Function to cache
 * @param options Memorization and LRUCache Options
 */
const memorise = (cachedFn, options = {}) => {
    // Extract defaults
    const { cache, cacheKeyResolver = defaultGenCacheKey, onHit, lruOptions = {}, } = options;
    const cacheOptions = { ...defaultLRUOptions, ...lruOptions };
    const _cache = (cache ||
        lru(cacheOptions.max, cacheOptions.ttl));
    // Cached fn
    function returnFn(...args) {
        const cacheKey = cacheKeyResolver(...args);
        const cachedValue = _cache.get(cacheKey);
        const keyCached = _cache.has(cacheKey);
        if (keyCached) {
            if (onHit) {
                onHit(cacheKey, cachedValue, _cache);
            }
            return cachedValue;
        }
        // No cache hit, run function and cache result
        const result = cachedFn.apply(this, args);
        _cache.set(cacheKey, result);
        return result;
    }
    // Expose the cache
    returnFn._cache = _cache;
    return returnFn;
};
/**
 * Generic args handler function.
 * @param args Argument array
 */
const defaultGenCacheKey = (...args) => {
    if (args.length === 0) {
        return "no-args";
    }
    return args
        .map((val) => {
        if (val === undefined) {
            return "undefined";
        }
        if (val === null) {
            return "null";
        }
        if (Array.isArray(val)) {
            return `[${defaultGenCacheKey(...val)}]`;
        }
        if (typeof val === "object") {
            return `{${defaultGenCacheKey(...sortedObjectEntries(val))}}`;
        }
        return JSON.stringify(val);
    })
        .join(",");
};
const sortedObjectEntries = (obj) => {
    return Object.entries(obj).sort((a, b) => {
        if (a[0] < b[0]) {
            return -1;
        }
        return 1;
    });
};

export { memorise as default, memorise };
